import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder,
  faFolderOpen,
  faFileAlt,
  faChevronRight,
  faChevronDown,
  faFolderPlus,
  faFileMedical,
  faPen,
  faTrash,
  faEllipsisV,
  faLock,
} from '@fortawesome/free-solid-svg-icons';
import { KbFolder, KbArticleStub, SelectedNode } from '../types';
import {
  SidebarContent,
  TreeItem,
  TreeItemName,
  TreeItemActions,
  IconBtn,
  MenuWrapper,
  DropdownMenu,
  DropdownItem,
  DropdownDivider,
} from '../styles';

type DragItem = { type: 'folder' | 'article'; id: number };

interface Props {
  folders: KbFolder[];
  articles: KbArticleStub[];
  selectedNode: SelectedNode | null;
  onSelectArticle: (id: number) => void;
  onSelectFolder: (id: number) => void;
  onCreateFolder: (parentId: number | null) => void;
  onRenameFolder: (id: number, currentName: string) => void;
  onDeleteFolder: (id: number) => void;
  onCreateArticle: (folderId: number | null) => void;
  onDeleteArticle: (id: number) => void;
  onMoveArticle: (articleId: number, folderId: number | null) => void;
  onMoveFolder: (folderId: number, parentId: number | null) => void;
}

interface FolderNodeProps {
  folder: KbFolder;
  depth: number;
  selectedNode: SelectedNode | null;
  dropTargetId: number | null;
  onSelectArticle: (id: number) => void;
  onSelectFolder: (id: number) => void;
  onCreateFolder: (parentId: number | null) => void;
  onRenameFolder: (id: number, currentName: string) => void;
  onDeleteFolder: (id: number) => void;
  onCreateArticle: (folderId: number | null) => void;
  onDeleteArticle: (id: number) => void;
  onDragStart: (item: DragItem) => void;
  onDragOverFolder: (folderId: number) => void;
  onDragLeave: () => void;
  onDropOnFolder: (folderId: number) => void;
  onDragEnd: () => void;
}

const FolderContextMenu: React.FC<{
  folder: KbFolder;
  onCreateFolder: (parentId: number) => void;
  onCreateArticle: (folderId: number) => void;
  onRenameFolder: (id: number, name: string) => void;
  onDeleteFolder: (id: number) => void;
}> = ({ folder, onCreateFolder, onCreateArticle, onRenameFolder, onDeleteFolder }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handle = (action: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    action();
  };

  return (
    <MenuWrapper ref={menuRef}>
      <IconBtn onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
        <FontAwesomeIcon icon={faEllipsisV} />
      </IconBtn>
      {open && (
        <DropdownMenu>
          <DropdownItem onClick={handle(() => onCreateFolder(folder.id))}>
            <FontAwesomeIcon icon={faFolderPlus} /> New subfolder
          </DropdownItem>
          <DropdownItem onClick={handle(() => onCreateArticle(folder.id))}>
            <FontAwesomeIcon icon={faFileMedical} /> New article
          </DropdownItem>
          <DropdownDivider />
          <DropdownItem onClick={handle(() => onRenameFolder(folder.id, folder.name))}>
            <FontAwesomeIcon icon={faPen} /> Rename
          </DropdownItem>
          {folder.children.length === 0 && folder.articles.length === 0 && (
            <>
              <DropdownDivider />
              <DropdownItem onClick={handle(() => onDeleteFolder(folder.id))} style={{ color: '#d32f2f' }}>
                <FontAwesomeIcon icon={faTrash} style={{ color: '#d32f2f' }} /> Delete
              </DropdownItem>
            </>
          )}
        </DropdownMenu>
      )}
    </MenuWrapper>
  );
};

const FolderNode: React.FC<FolderNodeProps> = ({
  folder, depth, selectedNode, dropTargetId,
  onSelectArticle, onSelectFolder, onCreateFolder, onRenameFolder, onDeleteFolder,
  onCreateArticle, onDeleteArticle,
  onDragStart, onDragOverFolder, onDragLeave, onDropOnFolder, onDragEnd,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isSelected = selectedNode?.type === 'folder' && selectedNode.id === folder.id;
  const isDropTarget = dropTargetId === folder.id;

  return (
    <>
      <TreeItem
        $depth={depth}
        $selected={isSelected}
        $dropTarget={isDropTarget}
        draggable
        onClick={() => { onSelectFolder(folder.id); setExpanded(!expanded); }}
        onDragStart={(e) => {
          e.stopPropagation();
          e.dataTransfer.effectAllowed = 'move';
          onDragStart({ type: 'folder', id: folder.id });
        }}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOverFolder(folder.id); }}
        onDragLeave={(e) => { e.stopPropagation(); onDragLeave(); }}
        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDropOnFolder(folder.id); }}
        onDragEnd={onDragEnd}
      >
        <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} style={{ fontSize: 10, width: 10 }} />
        <FontAwesomeIcon icon={expanded ? faFolderOpen : faFolder} style={{ color: '#f9a825', marginLeft: 4 }} />
        <TreeItemName>{folder.name}</TreeItemName>
        <TreeItemActions>
          <FolderContextMenu
            folder={folder}
            onCreateFolder={onCreateFolder}
            onCreateArticle={onCreateArticle}
            onRenameFolder={onRenameFolder}
            onDeleteFolder={onDeleteFolder}
          />
        </TreeItemActions>
      </TreeItem>
      {expanded && (
        <>
          {folder.children.map((child) => (
            <FolderNode
              key={'f-' + child.id}
              folder={child}
              depth={depth + 1}
              selectedNode={selectedNode}
              dropTargetId={dropTargetId}
              onSelectArticle={onSelectArticle}
              onSelectFolder={onSelectFolder}
              onCreateFolder={onCreateFolder}
              onRenameFolder={onRenameFolder}
              onDeleteFolder={onDeleteFolder}
              onCreateArticle={onCreateArticle}
              onDeleteArticle={onDeleteArticle}
              onDragStart={onDragStart}
              onDragOverFolder={onDragOverFolder}
              onDragLeave={onDragLeave}
              onDropOnFolder={onDropOnFolder}
              onDragEnd={onDragEnd}
            />
          ))}
          {folder.articles.map((art) => (
            <TreeItem
              key={'a-' + art.id}
              $depth={depth + 1}
              $selected={selectedNode?.type === 'article' && selectedNode.id === art.id}
              draggable={!art.protected}
              onClick={() => onSelectArticle(art.id)}
              onDragStart={(e) => {
                e.stopPropagation();
                e.dataTransfer.effectAllowed = 'move';
                onDragStart({ type: 'article', id: art.id });
              }}
              onDragEnd={onDragEnd}
            >
              <FontAwesomeIcon icon={art.protected ? faLock : faFileAlt} style={{ color: '#90a4ae', marginLeft: 14 }} />
              <TreeItemName>{art.title}</TreeItemName>
            </TreeItem>
          ))}
        </>
      )}
    </>
  );
};

const FolderTree: React.FC<Props> = ({
  folders, articles, selectedNode,
  onSelectArticle, onSelectFolder, onCreateFolder, onRenameFolder, onDeleteFolder,
  onCreateArticle, onDeleteArticle, onMoveArticle, onMoveFolder,
}) => {
  const [dragItem, setDragItem] = useState<DragItem | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);
  const [rootDropTarget, setRootDropTarget] = useState(false);

  const handleDragStart = useCallback((item: DragItem) => setDragItem(item), []);
  const handleDragOverFolder = useCallback((folderId: number) => {
    setDropTargetId(folderId);
    setRootDropTarget(false);
  }, []);
  const handleDragLeave = useCallback(() => setDropTargetId(null), []);
  const handleDragEnd = useCallback(() => { setDragItem(null); setDropTargetId(null); setRootDropTarget(false); }, []);

  const handleDropOnFolder = useCallback((targetFolderId: number) => {
    if (!dragItem) return;
    // Don't drop a folder onto itself
    if (dragItem.type === 'folder' && dragItem.id === targetFolderId) return;
    if (dragItem.type === 'article') {
      onMoveArticle(dragItem.id, targetFolderId);
    } else {
      onMoveFolder(dragItem.id, targetFolderId);
    }
    setDragItem(null);
    setDropTargetId(null);
  }, [dragItem, onMoveArticle, onMoveFolder]);

  const handleDropOnRoot = useCallback(() => {
    if (!dragItem) return;
    if (dragItem.type === 'article') {
      onMoveArticle(dragItem.id, null);
    } else {
      onMoveFolder(dragItem.id, null);
    }
    setDragItem(null);
    setRootDropTarget(false);
  }, [dragItem, onMoveArticle, onMoveFolder]);

  return (
    <SidebarContent
      onDragOver={(e) => { e.preventDefault(); setRootDropTarget(true); setDropTargetId(null); }}
      onDragLeave={() => setRootDropTarget(false)}
      onDrop={(e) => { e.preventDefault(); handleDropOnRoot(); }}
      style={rootDropTarget && !dropTargetId ? { background: '#e3f2fd' } : undefined}
    >
      {folders.map((folder) => (
        <FolderNode
          key={'f-' + folder.id}
          folder={folder}
          depth={0}
          selectedNode={selectedNode}
          dropTargetId={dropTargetId}
          onSelectArticle={onSelectArticle}
          onSelectFolder={onSelectFolder}
          onCreateFolder={onCreateFolder}
          onRenameFolder={onRenameFolder}
          onDeleteFolder={onDeleteFolder}
          onCreateArticle={onCreateArticle}
          onDeleteArticle={onDeleteArticle}
          onDragStart={handleDragStart}
          onDragOverFolder={handleDragOverFolder}
          onDragLeave={handleDragLeave}
          onDropOnFolder={handleDropOnFolder}
          onDragEnd={handleDragEnd}
        />
      ))}
      {articles.map((art) => (
        <TreeItem
          key={'a-' + art.id}
          $depth={0}
          $selected={selectedNode?.type === 'article' && selectedNode.id === art.id}
          draggable={!art.protected}
          onClick={() => onSelectArticle(art.id)}
          onDragStart={(e) => {
            e.stopPropagation();
            e.dataTransfer.effectAllowed = 'move';
            handleDragStart({ type: 'article', id: art.id });
          }}
          onDragEnd={handleDragEnd}
        >
          <FontAwesomeIcon icon={art.protected ? faLock : faFileAlt} style={{ color: '#90a4ae', marginLeft: 14 }} />
          <TreeItemName>{art.title}</TreeItemName>
        </TreeItem>
      ))}
    </SidebarContent>
  );
};

export default FolderTree;
